import { date } from '@/lib/date';
import { SacramentStatus } from 'generated/prisma/enums';
import z from 'zod';

const REQUIRED = 'Campo obrigatório.';
const CHOICE = 'Selecione uma opção.';

const requiredText = z.string(REQUIRED).trim().min(1, REQUIRED);

const phoneSchema = z
  .string(REQUIRED)
  .transform((val) => val.replace(/\D/g, ''))
  .refine((val) => /^\d{10,11}$/.test(val), {
    message: 'Informe um telefone válido.',
  });

const cepSchema = z
  .string(REQUIRED)
  .transform((val) => val.replace(/\D/g, ''))
  .refine((val) => /^\d{8}$/.test(val), {
    message: 'Informe um CEP válido.',
  });

const emailSchema = z.email('Informe um e-mail válido.');

const optionalText = z
  .string()
  .optional()
  .nullable()
  .transform((val) => val?.trim() || null);

const marriageDateSchema = z.preprocess(
  (val) => {
    if (!val) return undefined;
    if (typeof val === 'string' && val.trim() !== '') {
      return date(val).startOf('day').toDate();
    }
    return val;
  },
  z.date({ message: 'Informe a data.' }).optional(),
);

const sacramentSchema = z.enum(SacramentStatus, CHOICE);

const marriageDateRequired = (
  data: { marriageSacrament?: boolean; marriageDate?: Date },
  ctx: z.RefinementCtx,
) => {
  if (data.marriageSacrament && !data.marriageDate) {
    ctx.addIssue({
      code: 'custom',
      message: 'Informe a data.',
      path: ['marriageDate'],
    });
  }
};

export const createCoupleSchema = z
  .object({
    husbandName: requiredText,
    husbandPhone: phoneSchema,
    wifeName: requiredText,
    wifePhone: phoneSchema,
    email: emailSchema,

    cep: cepSchema,
    address: requiredText,
    addressNumber: requiredText,
    addressComplement: optionalText,

    baptismStatus: sacramentSchema,
    confirmationStatus: sacramentSchema,
    marriageSacrament: z.boolean(CHOICE),
    marriageDate: marriageDateSchema,

    parish: optionalText,
    pastoralMovement: optionalText,
    childrenInfo: optionalText,

    eventId: z.uuid('Selecione um encontro.'),
    isActive: z.boolean().optional(),
  })
  .superRefine(marriageDateRequired)
  .transform((data) => ({
    ...data,
    marriageDate: data.marriageSacrament ? data.marriageDate : undefined,
  }));

export const updateCoupleSchema = z
  .object({
    eventId: z.uuid('Selecione um encontro.').optional(),
    husbandName: requiredText.optional(),
    husbandPhone: phoneSchema.optional(),
    wifeName: requiredText.optional(),
    wifePhone: phoneSchema.optional(),
    email: emailSchema.optional(),

    cep: cepSchema.optional(),
    address: requiredText.optional(),
    addressNumber: requiredText.optional(),
    addressComplement: optionalText,

    baptismStatus: sacramentSchema.optional(),
    confirmationStatus: sacramentSchema.optional(),
    marriageSacrament: z.boolean(CHOICE).optional(),
    marriageDate: marriageDateSchema,

    parish: optionalText,
    pastoralMovement: optionalText,
    childrenInfo: optionalText,

    isActive: z.boolean().optional(),
  })
  .superRefine(marriageDateRequired)
  .transform((data) =>
    data.marriageSacrament === false ? { ...data, marriageDate: null } : data,
  );

export type CreateCoupleSchema = z.input<typeof createCoupleSchema>;
export type UpdateCoupleSchema = z.input<typeof updateCoupleSchema>;
